import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { message, Skeleton } from 'antd';
import { useMemo, useRef, useState } from 'react';

import { Alert, Button, Modal, Tag } from '../../../../../components/index';
import { useChangePagination } from '../../../../../hooks';
import {
  useGetMerchantsCeGroupbyNcmItemsQuery,
  useLazyGetMerchantsCeGroupbyNcmItemsQuery,
  useLazyGetNcmItemByIdQuery,
} from '../../../../../services/ncmItemApi';
import {
  CeMerchantGroupbyNcmItemType,
  NCMItemType,
} from '../../../../../types';
import { isNullOrUndefined } from '../../../../../utils/utils';
import { PanelHeader } from '../../../registers/panelHeader';
import { AssociateLoadingTable } from './associateLoadingTable/AssociateLoadingTable';
import { columns } from './associateLoadingTable/columns';
import { SelectAllItemsOperatorModal } from './selectAllItemsOperatorModal/SelectAllItemsOperatorModal';
import { SelectOperatorModal } from './selectOperatorModal/SelectOperatorModal';

type AssociateLoadingModalProps = {
  isVisible: boolean;
  handleCancelModal: () => void;
  selectedManifest: string;
  stopoverId: number;
  selectedOpDirectionManifest: string;
};

export function AssociateLoadingModal(props: AssociateLoadingModalProps) {
  const {
    isVisible,
    handleCancelModal,
    selectedManifest,
    stopoverId,
    selectedOpDirectionManifest,
  } = props;
  const { onChangePagination, queryPage, setQueryPage } = useChangePagination();
  const [searchQueryTerm, setSearchQueryTerm] = useState('');
  // Espelha o termo de busca atual de forma síncrona para que fetches em voo
  // consigam detectar se o filtro mudou no meio do caminho (o state capturado
  // na closure ficaria preso no valor antigo).
  const searchTermRef = useRef('');
  const [isVisibleOperatorModal, setIsVisibleOperatorModal] = useState(false);
  const [selectedLoading, setSelectedLoading] = useState(
    {} as CeMerchantGroupbyNcmItemType
  );
  // Lote selecionado; guardamos o registro inteiro (não só o id) para conseguir
  // resumir CE Mercante/consignatário da seleção. Acumula ao longo das páginas.
  const [selectedItems, setSelectedItems] = useState<
    CeMerchantGroupbyNcmItemType[]
  >([]);
  const selectedItemIds = useMemo(
    () => selectedItems.map((item) => item.ncm_item_id as number),
    [selectedItems]
  );
  // Modal de atribuição em lote aberto (sempre atua sobre o lote selecionado).
  const [isBatchAssignVisible, setIsBatchAssignVisible] = useState(false);
  // true quando o lote é "todas as cargas que correspondem à busca/manifesto"
  // (seleção estendida além da página atual, padrão de dois tempos).
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);

  const { data: ncmItemsData, isFetching: isFetchingNcmItemsData } =
    useGetMerchantsCeGroupbyNcmItemsQuery(
      {
        page: onChangePagination(),
        manifest_code: selectedManifest,
        stopover_id: stopoverId,
        operator_company_name_or_ncm_code_or_ce_merchant: searchQueryTerm,
      },
      { skip: isNullOrUndefined(selectedManifest) }
    );

  // adicionado apenas para pegar o total de cargas associadas
  const { data: totalNcmItems, isFetching: isFetchingTotalNcmItems } =
    useGetMerchantsCeGroupbyNcmItemsQuery(
      {
        manifest_code: selectedManifest,
        ncm_item_assigned: true,
        stopover_id: stopoverId,
      },
      {
        skip:
          isNullOrUndefined(selectedManifest) ||
          !ncmItemsData?.results ||
          ncmItemsData?.results.length === 0,
      }
    );

  const isVehicleManifest = useMemo(() => {
    if (ncmItemsData?.results && ncmItemsData?.results.length > 0) {
      return !isNullOrUndefined(ncmItemsData.results[0].chassis);
    }
    return false;
  }, [ncmItemsData]);

  const hasVolume = useMemo(() => {
    if (ncmItemsData?.results && ncmItemsData?.results.length > 0) {
      return !isNullOrUndefined(ncmItemsData.results[0].volume);
    }
    return false;
  }, [ncmItemsData]);

  const [getNcmItemById, { data: ncmItemByIdData }] =
    useLazyGetNcmItemByIdQuery();

  const [fetchFilteredItems, { isFetching: isFetchingFilteredBatch }] =
    useLazyGetMerchantsCeGroupbyNcmItemsQuery();

  function getTitle() {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
            <span style={{ color: 'var(--neutral_light)' }}>
              Manifesto {selectedManifest}
            </span>
            <Tag
              icon={
                selectedOpDirectionManifest === 'UNLOADING' ? (
                  <ArrowDownOutlined
                    style={{ color: 'var(--neutral_light)' }}
                  />
                ) : (
                  <ArrowUpOutlined style={{ color: 'var(--neutral_light)' }} />
                )
              }
            >
              {selectedOpDirectionManifest === 'UNLOADING'
                ? 'Descarga'
                : 'Carga'}
            </Tag>
          </div>
          <span
            style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--suape-black)',
            }}
          >
            Atribuir cargas
          </span>
        </div>
        <div>
          <Button
            type="link"
            style={{ color: 'var(--suape-black)' }}
            onClick={() => {
              clearSelection();
              handleCancelModal();
            }}
          >
            Voltar
          </Button>
          <Button
            type="primary"
            onClick={() => {
              clearSelection();
              handleCancelModal();
            }}
          >
            Concluir
          </Button>
        </div>
      </div>
    );
  }

  function clearSelection() {
    setSelectedItems([]);
    setAllMatchingSelected(false);
  }

  function searchData(value: string) {
    setQueryPage(1);
    setSearchQueryTerm(value);
    searchTermRef.current = value;
    // A seleção sempre reflete o filtro atual; ao mudar a busca, zera o lote.
    clearSelection();
  }

  function onSelectOperator(ncmItem: CeMerchantGroupbyNcmItemType) {
    setSelectedLoading(ncmItem);
    if (ncmItem.ncm_item_id) getNcmItemById(ncmItem.ncm_item_id);
    setIsVisibleOperatorModal(true);
  }

  function handleCancelSelectOperatorModal() {
    setIsVisibleOperatorModal(false);
    setSelectedLoading({} as CeMerchantGroupbyNcmItemType);
  }

  function handleCancelSelectAllItemsOperatorModal() {
    setIsBatchAssignVisible(false);
  }

  // Marca TODAS as cargas que correspondem ao filtro atual (todas as páginas),
  // pegando só as ainda não atribuídas.
  async function selectAllMatching() {
    // Sem total conhecido não há como pedir "todas as cargas" de uma vez;
    // o botão fica desabilitado enquanto a lista (e o count) carrega, mas
    // guardamos como salvaguarda para nunca enviar um page_size degenerado.
    const totalMatching = ncmItemsData?.count;
    if (!totalMatching) return;

    // Termo no início da requisição; se a busca mudar durante o fetch,
    // a resposta é do filtro antigo e deve ser descartada.
    const requestedTerm = searchTermRef.current;

    const response = await fetchFilteredItems({
      manifest_code: selectedManifest,
      stopover_id: stopoverId,
      operator_company_name_or_ncm_code_or_ce_merchant: requestedTerm,
      page_size: totalMatching,
    });

    // Filtro mudou no meio do caminho: não commita seleção obsoleta por cima
    // do clearSelection que o searchData já fez.
    if (requestedTerm !== searchTermRef.current) return;

    if (response.error) {
      message.error('Não foi possível selecionar as cargas. Tente novamente.');
      return;
    }

    const unassigned = (response.data?.results || []).filter((item) =>
      isNullOrUndefined(item.operator)
    );
    if (unassigned.length === 0) {
      message.warning('Todas as cargas já estão atribuídas.');
      return;
    }
    setSelectedItems(unassigned);
    setAllMatchingSelected(true);
  }

  // Checkbox do cabeçalho: marca tudo se ainda não está tudo marcado; senão limpa.
  function onToggleHeader() {
    if (allMatchingSelected) {
      clearSelection();
    } else {
      selectAllMatching();
    }
  }

  const totalAssociated = totalNcmItems?.count || 0;

  // O cabeçalho reflete a seleção global (todas as cargas do filtro):
  // marcado quando "todas" está ativo; indeterminado quando há seleção parcial.
  const headerChecked = allMatchingSelected;
  const headerIndeterminate =
    !allMatchingSelected && selectedItemIds.length > 0;

  return (
    <Modal
      className="TOSPrimaryModal"
      width={1300}
      title={getTitle()}
      visible={isVisible}
      destroyOnClose
      onCancel={() => handleCancelModal()}
      footer={null}
      closable={false}
    >
      <SelectOperatorModal
        isVisible={isVisibleOperatorModal}
        handleCancelModal={handleCancelSelectOperatorModal}
        selectedLoading={selectedLoading}
        ncmItem={ncmItemByIdData || ({} as NCMItemType)}
        selectedOpDirectionManifest={selectedOpDirectionManifest}
        isVehicleManifest={isVehicleManifest}
      />
      <SelectAllItemsOperatorModal
        isVisible={isBatchAssignVisible}
        handleCancelModal={handleCancelSelectAllItemsOperatorModal}
        selectedManifest={selectedManifest}
        countItems={ncmItemsData?.count || 0}
        stopoverId={stopoverId}
        selectedOpDirectionManifest={selectedOpDirectionManifest}
        ncmItemIds={selectedItemIds}
        selectedItems={selectedItems}
        remainingCount={(ncmItemsData?.count || 0) - totalAssociated}
        isVehicleManifest={isVehicleManifest}
        onSuccess={clearSelection}
      />
      <PanelHeader
        panelName="por consignatário, CE Mercante ou NCM"
        onSearch={searchData}
        inputWidth="430px"
        actionButtons={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              width: '100%',
              marginInlineStart: '15px',
            }}
          >
            <div style={{ flexShrink: 0 }}>
              {isFetchingTotalNcmItems || isFetchingNcmItemsData ? (
                <Skeleton.Input active />
              ) : (
                <span style={{ whiteSpace: 'nowrap' }}>
                  <strong>{totalAssociated}</strong> carga(s) atribuída(s)
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {selectedItemIds.length > 0 && (
                <span style={{ whiteSpace: 'nowrap' }}>
                  <strong>{selectedItemIds.length}</strong> selecionada(s)
                </span>
              )}
              <Button
                disabled={selectedItemIds.length === 0}
                onClick={() => setIsBatchAssignVisible(true)}
              >
                Atribuir operador aos selecionados
              </Button>
            </div>
          </div>
        }
      />

      {!isFetchingTotalNcmItems &&
        !isFetchingNcmItemsData &&
        (ncmItemsData?.count || 0) > 0 &&
        totalAssociated === (ncmItemsData?.count || 0) && (
          <Alert
            type="warning"
            showIcon
            message="Todas as cargas já estão atribuídas"
            style={{ margin: '8px 0' }}
          />
        )}

      <AssociateLoadingTable
        rowKey="ncm_item_id"
        columns={columns}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onUserToggle={() => setAllMatchingSelected(false)}
        headerChecked={headerChecked}
        headerIndeterminate={headerIndeterminate}
        onToggleHeader={onToggleHeader}
        isTogglingAll={isFetchingFilteredBatch || isFetchingNcmItemsData}
        onSelectItem={onSelectOperator}
        dataSource={ncmItemsData?.results || []}
        onChangePagination={onChangePagination}
        total={ncmItemsData?.count}
        isLoading={isFetchingNcmItemsData}
        queryPage={queryPage}
        showSizeChanger={false}
        isVehicleManifest={isVehicleManifest}
        selectedOpDirectionManifest={selectedOpDirectionManifest}
        hasVolume={hasVolume}
      />
    </Modal>
  );
}
